/**
 * Kleine prozedurale Soundkulisse ohne Downloads. Web Audio startet erst
 * nach dem LOSFAHREN-Tipp und respektiert damit Safari/Chrome-Autoplayregeln.
 *
 * Die wenigen dauerhaft laufenden Quellen sind absichtlich simpel gehalten:
 * ein gefilterter Motor, leises Ansaugrauschen und eine zweistimmige Hupe.
 */
export class AudioEngine {
  private context: AudioContext | null = null;

  private engineOsc: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;
  private intakeNoise: AudioBufferSourceNode | null = null;
  private intakeGain: GainNode | null = null;

  private hornOscillators: OscillatorNode[] = [];
  private hornGain: GainNode | null = null;
  private hornAudible = false;
  private muted = false;

  async enable(): Promise<void> {
    if (!this.context) this.create();
    await this.context?.resume();
  }

  private create(): void {
    const context = new AudioContext();
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -16;
    compressor.knee.value = 14;
    compressor.ratio.value = 5;
    compressor.attack.value = 0.005;
    compressor.release.value = 0.16;
    compressor.connect(context.destination);

    // Ein ungeradzahliger Obertonmix klingt eher nach Verbrennungspulsen als
    // ein nackter Sägezahn und bleibt trotzdem sehr günstig zu berechnen.
    const engineOsc = context.createOscillator();
    engineOsc.setPeriodicWave(context.createPeriodicWave(
      new Float32Array(7),
      new Float32Array([0, 1, 0.18, 0.38, 0.09, 0.16, 0.05]),
    ));
    engineOsc.frequency.value = 38;

    const engineFilter = context.createBiquadFilter();
    engineFilter.type = 'lowpass';
    engineFilter.frequency.value = 330;
    engineFilter.Q.value = 0.8;

    const engineGain = context.createGain();
    engineGain.gain.value = 0;
    engineOsc.connect(engineFilter).connect(engineGain);

    // Sehr leises, gefiltertes Rauschen nimmt dem Motor den sterilen
    // Synthesizerklang. Der kurze Loop belegt nur wenige Kilobyte.
    const intakeNoise = this.createNoiseLoop(context);
    const intakeFilter = context.createBiquadFilter();
    intakeFilter.type = 'bandpass';
    intakeFilter.frequency.value = 620;
    intakeFilter.Q.value = 0.65;
    const intakeGain = context.createGain();
    intakeGain.gain.value = 0.025;
    intakeNoise.connect(intakeFilter).connect(intakeGain).connect(engineGain);
    engineGain.connect(compressor);

    engineOsc.start();
    intakeNoise.start();

    // Zwei typische, leicht voneinander abweichende Hupenfrequenzen. Eine
    // resonante Obertonmischung und der Filter bilden Metallmembranen nach,
    // ohne ein Audio-Sample laden zu müssen.
    const hornWave = context.createPeriodicWave(
      new Float32Array(8),
      new Float32Array([0, 1, 0.34, 0.22, 0.12, 0.075, 0.045, 0.025]),
    );
    const hornFilter = context.createBiquadFilter();
    hornFilter.type = 'lowpass';
    hornFilter.frequency.value = 1900;
    hornFilter.Q.value = 0.7;

    const hornGain = context.createGain();
    hornGain.gain.value = 0;
    hornFilter.connect(hornGain).connect(compressor);

    const lowHorn = context.createOscillator();
    lowHorn.setPeriodicWave(hornWave);
    lowHorn.frequency.value = 410;
    lowHorn.detune.value = -3;
    const lowHornGain = context.createGain();
    lowHornGain.gain.value = 0.68;
    lowHorn.connect(lowHornGain).connect(hornFilter);

    const highHorn = context.createOscillator();
    highHorn.setPeriodicWave(hornWave);
    highHorn.frequency.value = 510;
    highHorn.detune.value = 4;
    const highHornGain = context.createGain();
    highHornGain.gain.value = 0.54;
    highHorn.connect(highHornGain).connect(hornFilter);

    lowHorn.start();
    highHorn.start();

    this.context = context;
    this.engineOsc = engineOsc;
    this.engineGain = engineGain;
    this.engineFilter = engineFilter;
    this.intakeNoise = intakeNoise;
    this.intakeGain = intakeGain;
    this.hornOscillators = [lowHorn, highHorn];
    this.hornGain = hornGain;
  }

  private createNoiseLoop(context: AudioContext): AudioBufferSourceNode {
    const frameCount = Math.max(1, Math.round(context.sampleRate * 0.28));
    const buffer = context.createBuffer(1, frameCount, context.sampleRate);
    const samples = buffer.getChannelData(0);
    let smoothed = 0;

    for (let i = 0; i < frameCount; i += 1) {
      smoothed = smoothed * 0.88 + (Math.random() * 2 - 1) * 0.12;
      samples[i] = smoothed * 2.2;
    }

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    return source;
  }

  update(speedKmh: number, throttle: number, horn: boolean, paused: boolean): void {
    const context = this.context;
    if (!context || !this.engineOsc || !this.engineGain || !this.engineFilter || !this.intakeGain || !this.hornGain) return;

    const now = context.currentTime;
    const safeSpeed = Math.max(0, Math.min(140, Math.abs(speedKmh)));
    const safeThrottle = Math.max(0, Math.min(1, throttle));
    const speedRatio = safeSpeed / 140;

    // Etwas unregelmäßiger Leerlauf sowie mehr Drehzahl unter Last. Die
    // Zeitkonstante verhindert hörbare Sprünge bei schwankenden FPS.
    const idleWobble = (1 - speedRatio) * (
      Math.sin(now * 7.1) * 0.8 + Math.sin(now * 11.7) * 0.35
    );
    const firingRate = 38 + Math.pow(speedRatio, 0.68) * 88 + safeThrottle * (17 - speedRatio * 7) + idleWobble;
    this.engineOsc.frequency.setTargetAtTime(firingRate, now, 0.055);
    this.engineFilter.frequency.setTargetAtTime(300 + firingRate * 2.1 + safeThrottle * 230, now, 0.09);
    this.intakeGain.gain.setTargetAtTime(0.018 + safeThrottle * 0.05, now, 0.12);

    const engineLevel = paused || this.muted ? 0 : 0.019 + safeThrottle * 0.029 + speedRatio * 0.006;
    this.engineGain.gain.setTargetAtTime(engineLevel, now, paused || this.muted ? 0.045 : 0.09);

    const hornShouldSound = horn && !paused && !this.muted;
    if (hornShouldSound !== this.hornAudible) {
      this.hornAudible = hornShouldSound;
      this.holdAutomationAtNow(this.hornGain.gain, now);
      this.hornGain.gain.setTargetAtTime(hornShouldSound ? 0.058 : 0, now, hornShouldSound ? 0.009 : 0.04);
    }
  }

  /** Verhindert Knackser, wenn die Hupe während ihrer Hüllkurve losgelassen wird. */
  private holdAutomationAtNow(parameter: AudioParam, now: number): void {
    if (typeof parameter.cancelAndHoldAtTime === 'function') {
      parameter.cancelAndHoldAtTime(now);
      return;
    }
    parameter.cancelScheduledValues(now);
    parameter.setValueAtTime(parameter.value, now);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (!muted || !this.context) return;

    const now = this.context.currentTime;
    this.engineGain?.gain.setTargetAtTime(0, now, 0.035);
    if (this.hornGain) {
      this.holdAutomationAtNow(this.hornGain.gain, now);
      this.hornGain.gain.setTargetAtTime(0, now, 0.025);
    }
    this.hornAudible = false;
  }

  dispose(): void {
    this.engineOsc?.stop();
    this.intakeNoise?.stop();
    for (const oscillator of this.hornOscillators) oscillator.stop();
    void this.context?.close();

    this.context = null;
    this.engineOsc = null;
    this.engineGain = null;
    this.engineFilter = null;
    this.intakeNoise = null;
    this.intakeGain = null;
    this.hornOscillators = [];
    this.hornGain = null;
    this.hornAudible = false;
  }
}
